import { useEffect, useState } from "react";
import {
    BadgePercent,
    Check,
    ChevronRight,
    Heart,
    PackageCheck,
    ShieldCheck,
    ShoppingCart,
    Star,
    Truck,
    X,
    Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../../../../../context/CartContext";
import { useWishlist } from "../../../../../../context/WishlistContext";
import { ProductType } from "../../../../../../Redux/custmoer_cart_service/types";
import {
    FashionProductItem,
    ProductColor,
    formatPrice,
} from "../../fashionTypes";
import "./MenProductOptionsModal.css";

export type { ProductColor };
export type MenFashionProduct = FashionProductItem;

type Props = {
    product: MenFashionProduct | null;
    onClose: () => void;
};

const MenProductOptionsModal = ({ product, onClose }: Props) => {
    const { addToCart, setCartOpen, cartItems } = useCart();
    const { isItemWishlisted, toggleWishlist: toggleWishlistContext } = useWishlist();
    const navigate = useNavigate();

    const [selectedColor, setSelectedColor] = useState(0);
    const [selectedSize, setSelectedSize] = useState("");
    const [wishlisted, setWishlisted] = useState(false);
    const [addedToBag, setAddedToBag] = useState(false);

    const optionLabel = product?.optionLabel || "Size";
    const availableOptions = product?.sizes || [];
    const hasOptions = availableOptions.length > 0;

    useEffect(() => {
        if (!product) return;

        setSelectedColor(0);
        const options = product.sizes || [];
        if (options.length > 0) {
            setSelectedSize(options.includes("M") ? "M" : options[0] || "");
        } else {
            setSelectedSize("");
        }
        setWishlisted(isItemWishlisted(String(product.id)));
        setAddedToBag(false);

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleEscape);
        };
    }, [product, onClose, isItemWishlisted]);

    if (!product) return null;

    const activeColorObj = product.colors && product.colors.length > 0
        ? product.colors[selectedColor] || product.colors[0]
        : null;
    const activeColorName = activeColorObj?.name;

    // Find matching variant based on selected color and option
    const matchedVariant = (product.variations || []).find((v: any) => {
        const attr = v.variationAttributes || v.attributes || {};
        const cVal = attr.Color || attr.color || attr.Colour || attr.colour;
        const colorMatches = !activeColorName || !cVal || String(cVal).toLowerCase().trim() === activeColorName.toLowerCase().trim();
        if (!hasOptions || !selectedSize) return colorMatches;

        const optVal = attr[optionLabel] || attr.Size || attr.size || attr.Storage || attr.storage || attr.Material || attr.material;
        const optionMatches = !optVal || String(optVal).toLowerCase().trim() === selectedSize.toLowerCase().trim();
        return colorMatches && optionMatches;
    }) || (product.variations || []).find((v: any) => {
        const attr = v.variationAttributes || v.attributes || {};
        const cVal = attr.Color || attr.color || attr.Colour || attr.colour;
        return activeColorName && cVal && String(cVal).toLowerCase().trim() === activeColorName.toLowerCase().trim();
    });

    const currentPrice = matchedVariant?.pricing?.finalPrice || matchedVariant?.pricing?.unitPrice || product.price;
    const currentMrp = Math.max(matchedVariant?.pricing?.mrp || product.mrp, currentPrice);
    const discount = currentMrp > currentPrice ? Math.round(((currentMrp - currentPrice) / currentMrp) * 100) : product.discountPercent;

    // Real coupon or savings (if available on the product)
    const couponSaving = Number(
        matchedVariant?.pricing?.couponDiscount ||
        product.rawProduct?.featuredOffer?.couponDiscount ||
        product.rawProduct?.couponDiscount ||
        0
    );
    const smartPrice = couponSaving > 0 ? Math.max(currentPrice - couponSaving, 1) : 0;

    const activeImage =
        matchedVariant?.mainImage ||
        matchedVariant?.images?.[0] ||
        matchedVariant?.media?.images?.[0] ||
        activeColorObj?.image ||
        product.image;

    const handleWishlistToggle = () => {
        setWishlisted((prev) => !prev);
        if (toggleWishlistContext) {
            toggleWishlistContext(String(product.id));
        }
    };

    const chosenColor = activeColorObj?.name;
    const chosenOption = hasOptions ? selectedSize : undefined;
    const variantLabel = [chosenColor, chosenOption].filter(Boolean).join(" / ") || undefined;
    const ssin = matchedVariant?.ssin || product.ssin || undefined;
    const resolvedVariantId = ssin || matchedVariant?._id || matchedVariant?.variantId || matchedVariant?.id || undefined;
    const effectiveProductId = String(product._id || product.rawProduct?._id || product.id || ssin || "");

    const isAlreadyInCart = (cartItems || []).some((item: any) => {
        const itemProdId = String(item.productId || item._id || item.id || "");
        const prodMatches =
            (effectiveProductId && itemProdId === effectiveProductId) ||
            (product._id && itemProdId === String(product._id)) ||
            (product.id && itemProdId === String(product.id)) ||
            (product.ssin && itemProdId === String(product.ssin)) ||
            (product.title && item.productName && item.productName.trim().toLowerCase() === product.title.trim().toLowerCase());

        if (!prodMatches) return false;

        if (resolvedVariantId && item.variantId) {
            return (
                String(item.variantId) === String(resolvedVariantId) ||
                (matchedVariant?.ssin && String(item.variantId) === String(matchedVariant.ssin)) ||
                (matchedVariant?._id && String(item.variantId) === String(matchedVariant._id))
            );
        }

        if (chosenColor && item.metadata?.color) {
            if (String(item.metadata.color).toLowerCase() !== String(chosenColor).toLowerCase()) {
                return false;
            }
        }
        if (chosenOption && item.metadata?.size) {
            if (String(item.metadata.size).toLowerCase() !== String(chosenOption).toLowerCase()) {
                return false;
            }
        }

        return true;
    });

    const addSelectedProductToCart = () => {
        addToCart({
            productId: effectiveProductId,
            productName: product.title,
            productImage: activeImage,
            productType: ProductType.PHYSICAL,
            variantId: resolvedVariantId,
            variantName: variantLabel,
            quantity: 1,
            unitPrice: currentPrice,
            price: currentPrice,
            mrp: currentMrp,
            sellerId: matchedVariant?.sellerId || product.rawProduct?.featuredOffer?.sellerId || product.rawProduct?.creatorId || undefined,
            sellerName: matchedVariant?.sellerName || product.rawProduct?.featuredOffer?.sellerName || product.rawProduct?.vendors?.marketedBy || product.brand?.name || undefined,
            isMLMProduct: false,
            isAdminProduct: product.rawProduct?.createdBy === "ADMIN",
            metadata: {
                color: chosenColor,
                size: chosenOption,
                mrp: currentMrp,
                image: activeImage,
                description: product.description,
            },
        });
    };

    const handleAddToCart = () => {
        addSelectedProductToCart();
        setAddedToBag(true);
        setCartOpen(true);
        const offcanvasElement = document.getElementById("offcanvasMiniCart");
        if (offcanvasElement) {
            const bootstrap = (window as any).bootstrap;
            if (bootstrap && bootstrap.Offcanvas) {
                const bsOffcanvas =
                    bootstrap.Offcanvas.getInstance(offcanvasElement) ||
                    new bootstrap.Offcanvas(offcanvasElement);
                bsOffcanvas.show();
            }
        }
    };

    const handleBuyNow = () => {
        addSelectedProductToCart();
        onClose();
        navigate("/Cart");
    };

    const handleViewDetails = () => {
        onClose();
        const targetId = matchedVariant?._id || matchedVariant?.id || product._id || product.id || product.ssin;
        const superCat = product.categoryHierarchy?.superCategory || "Fashion";
        const breadcrumb = [
            "Home",
            superCat,
            product.categoryHierarchy?.category,
            product.categoryHierarchy?.subCategory,
            product.title,
        ].filter(Boolean) as string[];

        navigate(`/DetailPage/${targetId}`, {
            state: {
                product: {
                    ...product,
                    category: superCat,
                    breadcrumb,
                },
            },
        });
    };

    const deliveryText =
        matchedVariant?.stock !== undefined && matchedVariant.stock < 5 && matchedVariant.stock > 0
            ? `Only ${matchedVariant.stock} left in stock`
            : product.delivery || "";

    return (
        <div
            className="men-options-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                className="men-options-modal"
                role="dialog"
                aria-modal="true"
                aria-label={`${product.title} options`}
            >
                <div className="men-options-mobile-handle" aria-hidden="true" />
                <button
                    type="button"
                    className="men-options-close"
                    onClick={onClose}
                    aria-label="Close product options"
                >
                    <X size={20} strokeWidth={1.8} />
                </button>

                <div className="men-options-left">
                    <div className="men-options-main-image">
                        {product.badge && (
                            <span className="men-options-image-badge">
                                {product.badge}
                            </span>
                        )}

                        <button
                            type="button"
                            className={`men-options-wishlist ${wishlisted ? "active" : ""}`}
                            onClick={handleWishlistToggle}
                            aria-label={
                                wishlisted
                                    ? "Remove from wishlist"
                                    : "Add to wishlist"
                            }
                        >
                            <Heart
                                size={19}
                                strokeWidth={2}
                                fill={wishlisted ? "currentColor" : "none"}
                            />
                        </button>

                        <img
                            src={activeImage}
                            alt={product.title}
                            onError={(e) => {
                                if (product.image && e.currentTarget.src !== product.image) {
                                    e.currentTarget.src = product.image;
                                } else {
                                    e.currentTarget.src =
                                        "/assets/images/products/men-fas/men-1.png";
                                }
                            }}
                        />
                    </div>

                    {product.colors && product.colors.length > 1 && (
                        <div className="men-options-preview-list">
                            {product.colors.map((color, index) => (
                                <button
                                    type="button"
                                    key={color.name || index}
                                    className={`men-options-preview ${
                                        selectedColor === index ? "active" : ""
                                    }`}
                                    onClick={() => setSelectedColor(index)}
                                    aria-label={`Preview ${color.name}`}
                                >
                                    <div className="men-options-preview-image">
                                        <img
                                            src={color.image || activeImage}
                                            alt={`${product.title} ${color.name}`}
                                            onError={(e) => {
                                                if (product.image && e.currentTarget.src !== product.image) {
                                                    e.currentTarget.src = product.image;
                                                }
                                            }}
                                        />
                                    </div>

                                    <span
                                        style={{
                                            backgroundColor: color.value,
                                        }}
                                    />

                                    <small>{color.name}</small>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="men-options-right">
                    <div className="men-options-brand">
                        <span>
                            {product.brand?.name ||
                                product.categoryHierarchy?.superCategory ||
                                "SALAR FASHION"}
                        </span>
                        <strong>{product.title}</strong>
                    </div>

                    {product.description && (
                        <p className="men-options-description">
                            {product.description}
                        </p>
                    )}

                    {(product.rating > 0 || (product.reviews && product.reviews !== "0")) && (
                        <div className="men-options-rating">
                            {product.rating > 0 && (
                                <strong>{product.rating.toFixed(1)}</strong>
                            )}

                            <span>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                        key={star}
                                        size={14}
                                        strokeWidth={1.7}
                                        fill={
                                            star <= Math.round(product.rating)
                                                ? "currentColor"
                                                : "none"
                                        }
                                    />
                                ))}
                            </span>

                            <small>{product.reviews} ratings</small>
                        </div>
                    )}

                    <div className="men-options-price">
                        <div className="men-options-current-price">
                            <sup>₹</sup>
                            {formatPrice(currentPrice)}
                        </div>

                        {currentMrp > currentPrice && (
                            <div className="men-options-mrp">
                                <span>MRP</span>
                                <del>₹{formatPrice(currentMrp)}</del>
                                {discount > 0 && (
                                    <strong>{discount}% OFF</strong>
                                )}
                            </div>
                        )}

                        <small>Inclusive of all taxes</small>
                    </div>

                    {couponSaving > 0 && (
                        <div className="men-options-smart-price">
                            <div className="men-options-smart-icon">
                                <BadgePercent
                                    size={19}
                                    strokeWidth={1.9}
                                />
                            </div>

                            <div className="men-options-smart-copy">
                                <span>SALAR SMART PRICE</span>
                                <strong>
                                    Unlock at ₹{formatPrice(smartPrice)}
                                </strong>
                                <small>
                                    Extra ₹{formatPrice(couponSaving)} savings with eligible Salar offers
                                </small>
                            </div>

                            <span className="men-options-smart-save">
                                EXTRA SAVE
                            </span>
                        </div>
                    )}

                    <div className="men-options-offer-card">
                        <div className="men-options-offer-label">
                            <span>S</span>

                            <div>
                                <strong>Salar Shopping Offer</strong>
                                <small>Exclusive benefit</small>
                            </div>
                        </div>

                        <div className="men-options-offer-copy">
                            <strong>Save more on this order</strong>

                            <p>
                                Use eligible Salar Shopping Voucher, reward
                                balance or promotional offer at checkout.
                            </p>

                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    navigate("/stuff/shoppingvoucher");
                                }}
                            >
                                View offers
                                <ChevronRight
                                    size={14}
                                    strokeWidth={2}
                                />
                            </button>
                        </div>
                    </div>

                    {product.colors && product.colors.length > 0 && (
                        <div className="men-options-selector">
                            <div className="men-options-selector-heading">
                                <div>
                                    <strong>Colours</strong>
                                    {activeColorName && (
                                        <span>{activeColorName}</span>
                                    )}
                                </div>
                            </div>

                            <div className="men-options-colours">
                                {product.colors.map((color, index) => (
                                    <button
                                        type="button"
                                        key={color.name || index}
                                        className={`men-options-colour ${
                                            selectedColor === index ? "active" : ""
                                        }`}
                                        onClick={() => setSelectedColor(index)}
                                    >
                                        <span
                                            style={{
                                                backgroundColor: color.value,
                                            }}
                                        />

                                        <small>{color.name}</small>

                                        {selectedColor === index && (
                                            <Check
                                                size={12}
                                                strokeWidth={2.5}
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {hasOptions && (
                        <div className="men-options-selector">
                            <div className="men-options-selector-heading">
                                <div>
                                    <strong>
                                        Select {optionLabel}
                                    </strong>
                                    {selectedSize && (
                                        <span>{selectedSize}</span>
                                    )}
                                </div>

                                {optionLabel.toLowerCase() === "size" && (
                                    <button type="button">
                                        Size guide
                                    </button>
                                )}
                            </div>

                            <div className="men-options-sizes">
                                {availableOptions.map((opt) => (
                                    <button
                                        type="button"
                                        key={opt}
                                        className={
                                            selectedSize === opt ? "active" : ""
                                        }
                                        onClick={() => setSelectedSize(opt)}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {deliveryText && (
                        <div className="men-options-delivery-row">
                            <div>
                                {product.deliveryType === "delivery" && (
                                    <Truck
                                        size={16}
                                        strokeWidth={1.8}
                                    />
                                )}

                                {product.deliveryType === "fast" && (
                                    <Zap
                                        size={16}
                                        strokeWidth={1.9}
                                    />
                                )}

                                {product.deliveryType === "stock" && (
                                    <PackageCheck
                                        size={16}
                                        strokeWidth={1.8}
                                    />
                                )}

                                <span>{deliveryText}</span>
                            </div>

                            <div>
                                <ShieldCheck
                                    size={16}
                                    strokeWidth={1.8}
                                />
                                <span>Secure purchase</span>
                            </div>
                        </div>
                    )}

                    <div className="men-options-primary-actions">
                        <button
                            type="button"
                            className="men-options-details"
                            onClick={handleViewDetails}
                        >
                            Product Details
                            <ChevronRight
                                size={15}
                                strokeWidth={2}
                            />
                        </button>

                        <button
                            type="button"
                            className={`men-options-add ${
                                addedToBag || isAlreadyInCart ? "added" : ""
                            }`}
                            onClick={handleAddToCart}
                        >
                            {addedToBag || isAlreadyInCart ? (
                                <>
                                    <Check
                                        size={17}
                                        strokeWidth={2.3}
                                    />
                                    Added to Cart
                                </>
                            ) : (
                                <>
                                    <ShoppingCart
                                        size={17}
                                        strokeWidth={2}
                                    />
                                    Add to Cart
                                </>
                            )}
                        </button>

                        <button
                            type="button"
                            className="men-options-buy"
                            onClick={handleBuyNow}
                        >
                            Buy Now
                            <ChevronRight
                                size={16}
                                strokeWidth={2}
                            />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MenProductOptionsModal;